import React, { Fragment, useState, useEffect } from "react";
import { 
  Alert, Badge, Button, Card, Col, Form, Modal, Row, Table, 
  InputGroup, Spinner, Toast, Pagination 
} from "react-bootstrap";

const BlockingManager = () => {
  // États principaux
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCards, setTotalCards] = useState(0);
  const pageSize = 20;
  
  // Filtres et recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [blockedFilter, setBlockedFilter] = useState('');
  
  // Modals et actions
  const [selectedCard, setSelectedCard] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // États pour les formulaires
  const [blockReason, setBlockReason] = useState('');
  const [customBlockReason, setCustomBlockReason] = useState('');
  const [isTemporaryBlock, setIsTemporaryBlock] = useState(false);
  const [blockExpiryDate, setBlockExpiryDate] = useState('');
  const [unblockReason, setUnblockReason] = useState('');
  const [blockHistory, setBlockHistory] = useState([]);
  
  // Notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  
  // Statistiques rapides
  const [quickStats, setQuickStats] = useState({
    totalCards: 0,
    blockedCards: 0,
    activeCards: 0
  });

  const predefinedBlockReasons = [
    'Perte signalée par le titulaire',
    'Vol signalé par le titulaire', 
    'Fraude suspectée',
    'Carte défectueuse',
    'Utilisation non autorisée',
    'Demande du titulaire',
    'Mesure préventive'
  ];

  const predefinedUnblockReasons = [
    'Carte retrouvée, vérification identité effectuée',
    'Fraude écartée après investigation',
    'Déblocage sur demande du titulaire',
    'Erreur de blocage initial',
    'Fin de la période de blocage temporaire',
    'Résolution du problème sécuritaire'
  ];

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Charger toutes les cartes avec pagination
  useEffect(() => {
    loadAllCards();
  }, [currentPage, searchTerm, statusFilter, blockedFilter]);

  useEffect(() => {
    loadQuickStats();
  }, []);

  const loadAllCards = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize,
        sortBy: 'ProductionDate',
        sortDirection: 'desc'
      });

      if (searchTerm) params.append('searchTerm', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await fetch(`${baseUrl}/Card/list?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        let cardsData = result.data || [];
        
        // Filtrer par statut de blocage si nécessaire
        if (blockedFilter === 'blocked') {
          cardsData = cardsData.filter(card => card.status === 'Blocked');
        } else if (blockedFilter === 'active') {
          cardsData = cardsData.filter(card => card.status !== 'Blocked');
        }
        
        setCards(cardsData);
        setTotalPages(result.totalPages || Math.ceil(cardsData.length / pageSize));
        setTotalCards(result.totalCount || cardsData.length);
      } else {
        throw new Error(`Erreur API: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des cartes:', error);
      setError('Impossible de charger les cartes');
      showToastMessage('Erreur lors du chargement des cartes', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadQuickStats = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const response = await fetch(`${baseUrl}/Card/statistics/production`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const stats = await response.json();
        setQuickStats({
          totalCards: stats.totalCards || 0,
          blockedCards: stats.failedCards || 0,
          activeCards: (stats.totalCards || 0) - (stats.failedCards || 0)
        });
      }
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };

  const blockCard = async () => {
    if (!selectedCard || !blockReason || (blockReason === 'custom' && !customBlockReason.trim())) {
      showToastMessage('Veuillez remplir tous les champs requis', 'warning');
      return;
    }

    setActionLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const requestBody = {
        reason: blockReason === 'custom' ? customBlockReason : blockReason,
        isTemporary: isTemporaryBlock,
        blockedBy: localStorage.getItem('userEmail') || 'admin@company.com',
        expiryDate: isTemporaryBlock ? blockExpiryDate : null
      };
      
      const response = await fetch(`${baseUrl}/Card/${selectedCard.id}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        showToastMessage(`Carte ${selectedCard.cardNumber} bloquée avec succès`, 'success');
        setShowBlockModal(false);
        resetBlockForm();
        loadAllCards();
        loadQuickStats();
      } else {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur lors du blocage:', error);
      showToastMessage(`Erreur lors du blocage: ${error.message}`, 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const unblockCard = async () => {
    if (!selectedCard || !unblockReason) {
      showToastMessage('Veuillez sélectionner une raison de déblocage', 'warning');
      return;
    }

    setActionLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const requestBody = {
        reason: unblockReason,
        unblockedBy: localStorage.getItem('userEmail') || 'admin@company.com'
      };
      
      const response = await fetch(`${baseUrl}/Card/${selectedCard.id}/unblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        showToastMessage(`Carte ${selectedCard.cardNumber} débloquée avec succès`, 'success');
        setShowUnblockModal(false);
        setUnblockReason('');
        loadAllCards();
        loadQuickStats();
      } else {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur lors du déblocage:', error);
      showToastMessage(`Erreur lors du déblocage: ${error.message}`, 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const loadBlockHistory = async (cardId) => {
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const response = await fetch(`${baseUrl}/Card/${cardId}/history`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const historyData = await response.json();
        const blockEvents = (historyData.data || []).filter(event => 
          event.action === 'Blocked' || event.action === 'Unblocked'
        );
        setBlockHistory(blockEvents);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setBlockHistory([]);
    }
  };

  const resetBlockForm = () => {
    setBlockReason('');
    setCustomBlockReason('');
    setIsTemporaryBlock(false);
    setBlockExpiryDate('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Blocked': { variant: 'danger', text: 'Bloquée', icon: 'la-ban' },
      'Delivered': { variant: 'success', text: 'Livrée', icon: 'la-check-circle' },
      'Shipped': { variant: 'info', text: 'Expédiée', icon: 'la-shipping-fast' },
      'Printing': { variant: 'primary', text: 'Impression', icon: 'la-print' },
      'QualityControl': { variant: 'warning', text: 'Contrôle', icon: 'la-shield-alt' },
      'PhotoProcessing': { variant: 'secondary', text: 'Photo', icon: 'la-image' },
      'DataValidation': { variant: 'info', text: 'Validation', icon: 'la-check-double' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status, icon: 'la-question' };
    return (
      <Badge bg={config.variant}>
        <i className={`la ${config.icon} me-1`}></i>
        {config.text}
      </Badge>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-muted">
          Affichage {((currentPage - 1) * pageSize) + 1} à {Math.min(currentPage * pageSize, totalCards)} sur {totalCards.toLocaleString()} cartes
        </div>
        <Pagination className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const startPage = Math.max(1, currentPage - 2);
            const page = startPage + i;
            if (page <= totalPages) {
              return (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Pagination.Item>
              );
            }
            return null;
          })}
          
          <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <Fragment>
      {/* Header avec statistiques */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-primary p-3 me-3">
                <i className="la la-layer-group text-white fs-4"></i>
              </div>
              <div>
                <h4 className="mb-0 fw-bold text-primary">{quickStats.totalCards.toLocaleString()}</h4>
                <small className="text-muted">Cartes totales</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-success p-3 me-3">
                <i className="la la-check-circle text-white fs-4"></i>
              </div>
              <div>
                <h4 className="mb-0 fw-bold text-success">{quickStats.activeCards.toLocaleString()}</h4>
                <small className="text-muted">Cartes actives</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-danger p-3 me-3">
                <i className="la la-ban text-white fs-4"></i>
              </div>
              <div>
                <h4 className="mb-0 fw-bold text-danger">{quickStats.blockedCards.toLocaleString()}</h4>
                <small className="text-muted">Cartes bloquées</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Barre de filtres */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-light border-0">
          <h6 className="mb-0 fw-bold">
            <i className="la la-filter me-2 text-primary"></i>
            Filtres et Recherche
          </h6>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label className="fw-medium">Recherche</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-white border-end-0">
                    <i className="la la-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Numéro de carte, bénéficiaire..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border-start-0"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label className="fw-medium">Statut de carte</Form.Label>
                <Form.Control
                  as="select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Tous les statuts</option>
                  <option value="DataValidation">Validation</option>
                  <option value="PhotoProcessing">Photo</option>
                  <option value="QualityControl">Contrôle qualité</option>
                  <option value="Printing">Impression</option>
                  <option value="Shipped">Expédiée</option>
                  <option value="Delivered">Livrée</option>
                  <option value="Blocked">Bloquée</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label className="fw-medium">Blocage</Form.Label>
                <Form.Control
                  as="select"
                  value={blockedFilter}
                  onChange={(e) => {
                    setBlockedFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Toutes les cartes</option>
                  <option value="active">Cartes actives seulement</option>
                  <option value="blocked">Cartes bloquées seulement</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setBlockedFilter('');
                  setCurrentPage(1);
                }}
              >
                <i className="la la-refresh me-2"></i>
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Liste des cartes */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold">
              <i className="la la-list me-2 text-primary"></i>
              Liste des Cartes
            </h6>
            <Badge bg="info" className="fs-6">
              {totalCards.toLocaleString()} cartes
            </Badge>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-3 text-muted">Chargement des cartes...</div>
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-5">
              <i className="la la-search la-4x text-muted mb-3" style={{opacity: 0.3}}></i>
              <h6 className="text-muted">Aucune carte trouvée</h6>
              <p className="text-muted">
                {searchTerm || statusFilter || blockedFilter 
                  ? 'Aucune carte ne correspond aux filtres appliqués'
                  : 'Aucune carte disponible'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="border-0 fw-bold">Carte</th>
                      <th className="border-0 fw-bold">Bénéficiaire</th>
                      <th className="border-0 fw-bold">Statut</th>
                      <th className="border-0 fw-bold">Date Production</th>
                      <th className="border-0 fw-bold">Informations</th>
                      <th className="border-0 fw-bold text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((card, index) => (
                      <tr key={card.id || index} className="align-middle">
                        <td>
                          <div className="d-flex align-items-center">
                            <div 
                              className={`me-3 rounded-circle d-flex align-items-center justify-content-center text-white ${
                                card.status === 'Blocked' ? 'bg-danger' : 'bg-primary'
                              }`}
                              style={{ width: '40px', height: '40px' }}
                            >
                              <i className={`la ${card.status === 'Blocked' ? 'la-ban' : 'la-id-card'}`}></i>
                            </div>
                            <div>
                              <div className="fw-bold font-monospace">{card.cardNumber}</div>
                              <small className="text-muted">ID: {card.id}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {card.beneficiaryName || `Bénéficiaire ${card.beneficiaryId}`}
                          </div>
                          <small className="text-muted">ID: {card.beneficiaryId}</small>
                        </td>
                        <td>
                          {getStatusBadge(card.status)}
                        </td>
                        <td>
                          <div>{card.productionDate ? new Date(card.productionDate).toLocaleDateString('fr-FR') : 'N/A'}</div>
                          {card.productionDate && (
                            <small className="text-muted">
                              {new Date(card.productionDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </small>
                          )}
                        </td>
                        <td>
                          {card.trackingNumber && (
                            <div className="text-info mb-1">
                              <i className="la la-shipping-fast me-1"></i>
                              <small>{card.trackingNumber}</small>
                            </div>
                          )}
                          {card.deliveryDate && (
                            <div className="text-success mb-1">
                              <i className="la la-check-circle me-1"></i>
                              <small>{new Date(card.deliveryDate).toLocaleDateString('fr-FR')}</small>
                            </div>
                          )}
                          {card.blockReason && (
                            <div className="text-danger">
                              <i className="la la-exclamation-triangle me-1"></i>
                              <small>{card.blockReason}</small>
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            {card.status === 'Blocked' ? (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => {
                                  setSelectedCard(card);
                                  loadBlockHistory(card.id);
                                  setShowUnblockModal(true);
                                }}
                                title="Débloquer"
                              >
                                <i className="la la-unlock me-1"></i>
                                Débloquer
                              </Button>
                            ) : (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedCard(card);
                                  setShowBlockModal(true);
                                }}
                                title="Bloquer"
                              >
                                <i className="la la-ban me-1"></i>
                                Bloquer
                              </Button>
                            )}
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                setSelectedCard(card);
                                loadBlockHistory(card.id);
                                setShowHistoryModal(true);
                              }}
                              title="Historique"
                            >
                              <i className="la la-history"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              {totalPages > 1 && (
                <Card.Footer className="bg-light border-0">
                  {renderPagination()}
                </Card.Footer>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Toast Notifications */}
      <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1050 }}>
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          autohide 
          delay={4000}
          className="border-0 shadow"
        >
          <Toast.Header className={`bg-${toastVariant} text-white border-0`}>
            <i className={`la ${
              toastVariant === 'success' ? 'la-check-circle' :
              toastVariant === 'danger' ? 'la-exclamation-triangle' :
              toastVariant === 'warning' ? 'la-exclamation-circle' :
              'la-info-circle'
            } me-2`}></i>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Succès' :
               toastVariant === 'danger' ? 'Erreur' :
               toastVariant === 'warning' ? 'Attention' :
               'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </div>

      {/* Modal de blocage - Version simplifiée */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-danger text-white border-0">
          <Modal.Title>
            <i className="la la-ban me-2"></i>
            Bloquer la carte - {selectedCard?.cardNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCard && (
            <>
              <Alert variant="warning">
                <div className="d-flex align-items-center">
                  <i className="la la-exclamation-triangle me-3 fs-3"></i>
                  <div>
                    <strong>Attention!</strong> Cette action bloquera l'utilisation de la carte.
                    <br />
                    <small>Carte: <strong>{selectedCard.cardNumber}</strong> | Bénéficiaire: <strong>{selectedCard.beneficiaryName || selectedCard.beneficiaryId}</strong></small>
                  </div>
                </div>
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label className="fw-medium">Raison du blocage *</Form.Label>
                <Form.Control
                  as="select"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                >
                  <option value="">-- Sélectionner une raison --</option>
                  {predefinedBlockReasons.map((reason, index) => (
                    <option key={index} value={reason}>{reason}</option>
                  ))}
                  <option value="custom">Autre raison (préciser)</option>
                </Form.Control>
              </Form.Group>

              {blockReason === 'custom' && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Préciser la raison *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={customBlockReason}
                    onChange={(e) => setCustomBlockReason(e.target.value)}
                    placeholder="Expliquez la raison du blocage..."
                  />
                </Form.Group>
              )}

              <Row>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="temporary-block"
                    label="Blocage temporaire"
                    checked={isTemporaryBlock}
                    onChange={(e) => setIsTemporaryBlock(e.target.checked)}
                    className="mb-3"
                  />
                </Col>
              </Row>

              {isTemporaryBlock && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Date de fin de blocage</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={blockExpiryDate}
                    onChange={(e) => setBlockExpiryDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                  <Form.Text className="text-muted">
                    La carte sera automatiquement débloquée à cette date.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowBlockModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="danger" 
            onClick={blockCard}
            disabled={!blockReason || (blockReason === 'custom' && !customBlockReason.trim()) || actionLoading}
          >
            {actionLoading ? (
              <Spinner size="sm" animation="border" className="me-2" />
            ) : (
              <i className="la la-ban me-2"></i>
            )}
            {isTemporaryBlock ? 'Bloquer temporairement' : 'Bloquer définitivement'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de déblocage - Version simplifiée */}
      <Modal show={showUnblockModal} onHide={() => setShowUnblockModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-success text-white border-0">
          <Modal.Title>
            <i className="la la-unlock me-2"></i>
            Débloquer la carte - {selectedCard?.cardNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCard && (
            <>
              <Alert variant="info">
                <div className="d-flex align-items-center">
                  <i className="la la-info-circle me-3 fs-3"></i>
                  <div>
                    <strong>Information:</strong> Cette action réactivera l'utilisation de la carte.
                    <br />
                    <small>Carte: <strong>{selectedCard.cardNumber}</strong> | Statut: <Badge bg="danger">Bloquée</Badge></small>
                  </div>
                </div>
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label className="fw-medium">Raison du déblocage *</Form.Label>
                <Form.Control
                  as="select"
                  value={unblockReason}
                  onChange={(e) => setUnblockReason(e.target.value)}
                >
                  <option value="">-- Choisir une raison --</option>
                  {predefinedUnblockReasons.map((reason, index) => (
                    <option key={index} value={reason}>{reason}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              {blockHistory.length > 0 && (
                <div>
                  <h6 className="text-muted mb-3">
                    <i className="la la-history me-2"></i>
                    Historique récent
                  </h6>
                  <div className="border rounded p-3 bg-light">
                    {blockHistory.slice(0, 3).map((event, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <Badge bg={event.action === 'Blocked' ? 'danger' : 'success'}>
                            {event.action === 'Blocked' ? 'Bloqué' : 'Débloqué'}
                          </Badge>
                          <span className="ms-2">{event.description}</span>
                        </div>
                        <small className="text-muted">
                          {new Date(event.timestamp).toLocaleString('fr-FR')}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowUnblockModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="success" 
            onClick={unblockCard}
            disabled={!unblockReason || actionLoading}
          >
            {actionLoading ? (
              <Spinner size="sm" animation="border" className="me-2" />
            ) : (
              <i className="la la-unlock me-2"></i>
            )}
            Débloquer la carte
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Historique - Version simplifiée */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="la la-history me-2"></i>
            Historique - {selectedCard?.cardNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {blockHistory.length > 0 ? (
            <div>
              {blockHistory.map((event, index) => (
                <div key={index} className="d-flex mb-3">
                  <div className="flex-shrink-0 me-3">
                    <div className={`rounded-circle d-flex align-items-center justify-content-center text-white ${
                      event.action === 'Blocked' ? 'bg-danger' : 'bg-success'
                    }`} style={{width: '40px', height: '40px'}}>
                      <i className={`la ${event.action === 'Blocked' ? 'la-ban' : 'la-unlock'}`}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="card border-0 bg-light">
                      <div className="card-body py-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-1">{event.action === 'Blocked' ? 'Carte bloquée' : 'Carte débloquée'}</h6>
                          <small className="text-muted">
                            {new Date(event.timestamp).toLocaleString('fr-FR')}
                          </small>
                        </div>
                        <p className="mb-1">{event.description}</p>
                        {event.performedBy && (
                          <small className="text-muted">
                            <i className="la la-user me-1"></i>
                            Par: {event.performedBy}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="la la-history la-4x text-muted mb-3" style={{opacity: '0.3'}}></i>
              <h6 className="text-muted">Aucun historique de blocage</h6>
              <p className="text-muted mb-0">Cette carte n'a jamais été bloquée ou débloquée.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default BlockingManager;